import { Policy } from '~/contexts/AuthenticationContext';
import { Permission } from '~/apis/ssf';

function isClubAdmin(permissions: Permission, entityId?: number) {
  if (!entityId) {
    return false;
  }

  return !!permissions.clubs_admin.find(c => c.id === Number(entityId));
}

function isCompetitionAdmin(permissions: Permission, entityId?: number) {
  if (!entityId) {
    return false;
  }

  return !!permissions.competitions_admin.find(c => c.id === Number(entityId));
}

function isDistrictAdmin(permissions: Permission, entityId?: number) {
  if (!entityId) {
    return false;
  }

  return !!permissions.districts_admin.find(d => d.id === Number(entityId));
}

export function isAnyClubAdmin(permissions: Permission) {
  return permissions.clubs_admin.length > 0;
}

function isAnyDistrictAdmin(permissions: Permission) {
  return permissions.districts_admin.length > 0;
}

function isRefereeAdmin(permissions: Permission) {
  return !!permissions.judge_admin;
}

export function checkUserPolicy(
  permissions: Permission,
  policy: Policy,
  entityId: number | undefined,
  checkAnyClubAdmin: boolean | undefined
): boolean {
  if (permissions.federation_admin) {
    return true;
  }

  switch (policy) {
    /**
     * Clubs
     */
    case 'create-club':
    case 'delete-club':
      return false;
    case 'edit-club':
      return isClubAdmin(permissions, entityId);

    /**
     * Club admins
     */
    case 'list-club-admin':
    case 'create-club-admin':
    case 'delete-club-admin':
      return isClubAdmin(permissions, entityId);

    /**
     * Competitions
     */
    case 'create-competition':
      return isAnyClubAdmin(permissions) || isAnyDistrictAdmin(permissions);
    case 'export-competition-registrations':
    case 'manage-competition-integrations':
    case 'see-competition-api-key':
    case 'delete-competition':
    case 'edit-competition':
      return isCompetitionAdmin(permissions, entityId);

    /**
     * Competition registrations
     */
    case 'list-competition-registration':
      return checkAnyClubAdmin
        ? isAnyClubAdmin(permissions) || isCompetitionAdmin(permissions, entityId)
        : isCompetitionAdmin(permissions, entityId);
    case 'create-competition-registration':
      return checkAnyClubAdmin
        ? isAnyClubAdmin(permissions) || isCompetitionAdmin(permissions, entityId)
        : isCompetitionAdmin(permissions, entityId);
    case 'edit-competition-registration':
      return checkAnyClubAdmin
        ? isAnyClubAdmin(permissions) || isCompetitionAdmin(permissions, entityId)
        : isCompetitionAdmin(permissions, entityId);
    case 'delete-competition-registration':
      return checkAnyClubAdmin
        ? isAnyClubAdmin(permissions) || isCompetitionAdmin(permissions, entityId)
        : isCompetitionAdmin(permissions, entityId);
    case 'see-competition-registration-status':
      return isCompetitionAdmin(permissions, entityId);
    case 'manual-approve-registration':
        return isCompetitionAdmin(permissions, entityId);

    /**
     * Competition results
     */
    case 'manage-competition-result':
      return isCompetitionAdmin(permissions, entityId);

    /**
     * Competition divisions and teams (lagtävlingar)
     */
    case 'manage-competition-division':
      return isCompetitionAdmin(permissions, entityId);
    case 'manage-competition-team':
      return isCompetitionAdmin(permissions, entityId);

    /**
     * Districts
     */
    case 'create-district':
    case 'delete-district':
      return false;
    case 'edit-district':
      return isDistrictAdmin(permissions, entityId);

    /**
     * District admins
     */
    case 'list-district-admin':
    case 'delete-district-admin':
    case 'create-district-admin':
      return isDistrictAdmin(permissions, entityId);

    /**
     * Lifters
     */
    case 'create-lifter':
      return isAnyClubAdmin(permissions);
    case 'edit-lifter':
      return isClubAdmin(permissions, entityId);
    case 'delete-lifter':
      return false;

    /**
     * Lifter licenses
     */
    case 'list-lifter-license':
    case 'create-lifter-license':
      return isClubAdmin(permissions, entityId);
    case 'edit-lifter-license':
      return false;
    case 'delete-lifter-license':
      return false;

    /**
     * Referees
     */
    case 'create-referee':
    case 'edit-referee':
    case 'delete-referee':
      return isRefereeAdmin(permissions);

    /**
     * Referee licenses
     */
    case 'list-referee-license':
    case 'create-referee-license':
    case 'edit-referee-license':
    case 'delete-referee-license':
      return isRefereeAdmin(permissions);

    /**
     * Referee admin
     */
    case 'list-referee-admin':
      return isRefereeAdmin(permissions);

    /**
     * User management
     */
    case 'manage-users':
      return false;

    /**
     * Standard records
     */
    case 'manage-standard-records':
      return isAnyDistrictAdmin(permissions);

    /**
     * Records
     */
    case 'manage-records':
      return isAnyDistrictAdmin(permissions);

    /**
     * Series
     */
    case 'manage-series':
    case 'create-division':
    case 'edit-division':
    case 'delete-division':
    case 'manage-round':
    case 'manage-team':
      return false;

    case 'manage-round-result':
      return isClubAdmin(permissions, entityId);

    default:
      return false;
  }
}
